import React, { useState, useEffect, useContext, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { useForm } from "react-hook-form";
import { useMutation, ClientContext } from "graphql-hooks";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Link as RouterLink } from "react-router-dom";

import {
  Card,
  CardContent,
  Container,
  Divider,
  Link,
  Typography,
  Button,
  FormHelperText,
  TextField,
  Grid,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import logoImg from "./assets/logo.png";
import loginImg from "./assets/login-image.png";
import mascotJoin from "./assets/login-mascot.png";
import useIsTablet from "./hooks/useIsTablet";
import useIsMobile from "./hooks/useIsMobile";

import { FORGET_PASSWORD_MUTATION } from "./graphQL/user_qry";
const domain_name = process.env.REACT_APP_DOMAIN_URL || "https://r3play.com";

const StyledContainer = styled(Container)(() => ({
            
    minHeight: 624,
    angle: "0 deg",
    opacity: 1,
    borderRadius: "16px",
    boxShadow: "0px 4px 12px 0px #0000001F",
    display: "flex",
    justifyContent: "center",
    justifyItems: "center" ,

    // DESKTOP
    maxWidth: "1080px",
    margin: "48px 150px",

    "@media (max-width: 1360px)": {
        maxWidth: "802px",      // TABLET WIDTH
        margin: "32px auto",
    },

    "@media (max-width: 856px)": {
        maxWidth: "361px",      // MOBILE WIDTH
        margin: "8px auto",
    },
    
}));

const FormSide = styled(Box)(() => ({
  
   width: "540px",             // Desktop 2 columns = 1080 total
   padding: "32px",
   gap: "16px",

  "@media (max-width: 1360px)": 
  { width: "401px" },// Tablet 2 columns = 802 total
  
  "@media (max-width: 856px)":  
  {  width: "100%",            // Mobile = single column
     padding: "16px",          // Reduce padding on mobile },
     height: "auto",
     minHeight: "auto"
  },
  minHeight: 624,
  
  angle: "0 deg",
  opacity: 1,
}));

const StyledTextField = styled(TextField)(() => ({
    marginBottom: 5,
    marginTop: 30,
    "& .MuiInputLabel-outlined": {
      fontSize: 16,
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      fontSize: 16,
    },
}));

const StyledFormHelperTextError = styled(FormHelperText)(() => ({
    marginTop: 0,
    fontSize: 14, //16,
    fontFamily: "Arial",
}));

const StyledBox  = styled(Box)(() => ({
    color: "#212121",
    backgroundColor: "rgba(239, 86, 88, 0.1)",
    borderRadius: 12,
    padding: 16,
    fontSize: 16,
    fontFamily: "Arial",
    lineHeight: 1.4,
}));

const StyledButton = styled(Button)(() => ({
    width: "158px",
    height: "48px",
    backgroundColor: "#EF5658",
    textTransform: "none",
    "&:active": {
      backgroundColor: "#EF5658",
    },
    "&:hover": {
      backgroundColor: "#D33338",
    },
}));

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

const ResetPassword = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const navigate = useNavigate();
  const form = useRef(null);
    
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [email, setEmail] = useState("");

  const [forgetPassword] = useMutation(FORGET_PASSWORD_MUTATION);

  const isTablet = useIsTablet(1360); // Use 1360px breakpoint for tablet size
  const isMobile = useIsMobile(856); // Use 856px breakpoint for mobile size

  setTimeout(() => {
    setSuccessMsg("");
    setErrorMsg("");
  }, 30000);

  const onSubmit = async formData => {
    const { data, error } = await forgetPassword({
      variables: {
        email: formData.email,
      },
    });
    if (error) {
      console.log("Error", error, data);
      setErrorMsg(error.graphQLErrors[0].message);
      setTimeout("");
    } else {
      document.getElementById("email").value = "";
      setSuccessMsg(
        "An email will be sent to the email address you provided. Please follow the instructions in that email."
      );
      setTimeout("");
      // navigate("/ie/signin");
    }
  };

  const emailChanged = event => {
    setEmail(event.target.value);
  };

    useEffect(() => {
    window.scrollTo(0, 0);    
  }, []);

  return (
    <Grid>
      <>
        <Helmet>
          <title>R3PLAY - Reset Password</title>
          <meta
            name="description"
            content="Reset your account password securely and regain access"
          />
          <meta
            property="keywords"
            content="Reset password, recover account, toy marketplace login"
          />        
        </Helmet>
        <form ref={form} onSubmit={handleSubmit(onSubmit)}>            
          <Box
            sx={{
              position: "relative",
              display: "flex",
              alignItems: "center",
            }}
          >
           {!isMobile ?
            (
              <StyledContainer>
              <Box                  
                  display="grid"
                  gridTemplateColumns="1fr 1fr"                 
                  justifyContent="center"
                  justifyItems="center"       // center the fixed-width children
                  alignItems="start"
                  gap="0"
                  sx={{ width: "100%" }}
                >
                <FormSide sx={{ borderRight: "1px solid #e0e0e0" }}>
                    <Box
                      flexGrow={1}
                      alignItems="center"
                      display="flex"
                      justifyContent="space-between"
                    >
                     <Typography gutterBottom 
                        variant={!isTablet ? "h2" : "h3"}
                        fontSize={!isTablet ? "36px" : "30px"}
                        sx={{                        
                        fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                        fontWeight: "600",
                        lineHeight: "1.75",
                        letterSpacing: "0.00938em"
                      }}>

                        Reset Password
                      </Typography>
                    </Box>

                    <Box flexGrow={1} mb={1}>
                      <StyledTextField
                        fullWidth
                        //autoFocus
                        label="Email" // Address"
                        margin="normal"
                        name="email"
                        type="email"
                        variant="outlined"
                        id="email"
                        {...register("email", {
                          required: true,
                          pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                        })}
                      />
                      {errors.email && errors.email.type === "required" && (
                        <StyledFormHelperTextError>
                          Email address cannot be blank.
                        </StyledFormHelperTextError>
                      )}
                      {errors.email && errors.email.type === "pattern" && (
                        <StyledFormHelperTextError>
                          Enter a correctly-formed email like myname@xyz.com
                        </StyledFormHelperTextError>
                      )}
                      {errorMsg && (
                        <Box mt={1} mb={1} ml={2}>
                          <StyledFormHelperTextError>
                            {errorMsg}
                          </StyledFormHelperTextError>
                        </Box>
                      )}
                      {successMsg && (
                        <StyledBox mt={2}>
                          {successMsg}
                        </StyledBox>
                      )}
                      
                      <Box mt={4} display="flex">
                        <StyledButton
                          color="primary"
                          type="submit"
                          variant="contained"
                        >
                          SUBMIT
                        </StyledButton>
                      </Box>
                    </Box>

                    <Box mt={4} 
                         sx={{
                          fontSize: "14px",
                          fontStyle: "normal",
                          fontFamily: "Roboto,Helvetica,Arial,sans-serif",
                          fontWeight: "400",
                          lineHeight: "1.7",
                          letterSpacing: "0.00938em",
                          textAlign: "left", // center text on mobile
                        }}>

                      <Typography variant="body2">
                        Don't have an account yet?
                        <Link ml={1} mr={1} component={RouterLink} to="/signup" color="primary" underline="none">
                          Sign Up
                        </Link>
                      </Typography>

                    </Box>
                  </FormSide>

                  <FormSide sx={{ paddingLeft: "0" }}>
                    <Box flexGrow={1}>
                      <Box sx={{paddingLeft: "32px"}}>
                        <img src={logoImg} alt="logo" width={"40%"} />
                      </Box>
                      <Typography variant="body1"
                        sx={{
                        color: "rgba(33, 33, 33, 0.8)",
                        fontSize: "1rem",
                        fontStyle: "normal",
                        fontFamily: "Roboto,Helvetica,Arial,sans-serif",
                        fontWeight: "400",
                        lineHeight: "1.7",
                        letterSpacing: "0.00938em",
                        paddingLeft: "32px"
                        }}>
                        <br />
                        <b>No worries, it happens!</b>
                        <br /><br />
                        Even the best players need a reset sometimes.
                        <br /><br />
                        Enter your email and we will help you get back into the game — your toys, your trades, and your memories are waiting for you.
                        <br /><br />
                        Let’s get you back to R3PLAY!
                      </Typography>
                      <Box>
                        <img src={loginImg} alt="logo" style={{
                                  width: isTablet ? "420px" : "540px",
                                 }} /> 
                      </Box>
                    </Box>                    
                  </FormSide>
                </Box>
              </StyledContainer>
               )
            :
            (
              <StyledContainer>
              <Box                  
                  display="grid"
                  gridTemplateColumns="1fr"                  
                  justifyContent="center"
                  justifyItems="center"       // center the fixed-width children                  
                  gap="0"                
                  sx={{ width: "100%" }}
                >

                <FormSide >

                  <Box
                      flexGrow={1}
                      alignItems="center"
                      display="flex"
                      justifyContent="space-between"
                    >
                      <Typography gutterBottom 
                        variant="h3"
                        fontSize="30px"
                        sx={{                        
                        fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                        fontWeight: "600",
                        lineHeight: "1.75",
                        letterSpacing: "0.00938em"
                      }}>
                        Reset Password
                      </Typography>
                    </Box>
                    
                    <Box flexGrow={1}>                     
                      <Typography variant="body1"
                        sx={{
                        color: "rgba(33, 33, 33, 0.8)",
                        fontSize: "1rem",
                        fontStyle: "normal",
                        fontFamily: "Roboto,Helvetica,Arial,sans-serif",
                        fontWeight: "400",
                        lineHeight: "1.7",
                        letterSpacing: "0.00938em",
                        
                        }}>
                        <br />
                        <b>No worries, it happens!</b>
                        <br /><br />
                        Even the best players need a reset sometimes.
                        <br /><br />
                        Enter your email and we will help you get back into the game — your toys, your trades, and your memories are waiting for you.
                        <br /><br />
                        Let’s get you back to R3PLAY!
                      </Typography>
                      <Box>
                            <img src={loginImg} alt="logo" width={"100%"} />
                      </Box>
                      
                    </Box>                    
                  </FormSide>

                  <FormSide sx={{ borderRight: "none" }}>
                    

                  <Box flexGrow={1} mb={1}>
                  <StyledTextField
                        fullWidth
                        //autoFocus
                        label="Email" // Address"
                        margin="normal"
                        name="email"
                        type="email"
                        variant="outlined"
                        id="email"
                        {...register("email", {
                          required: true,
                          pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                        })}
                      />
                      {errors.email && errors.email.type === "required" && (
                        <StyledFormHelperTextError>
                          Email address cannot be blank.
                        </StyledFormHelperTextError>
                      )}
                      {errors.email && errors.email.type === "pattern" && (
                        <StyledFormHelperTextError>
                          Enter a correctly-formed email like myname@xyz.com
                        </StyledFormHelperTextError>
                      )}
                      {errorMsg && (
                        <Box mt={1} mb={1} ml={2}>
                          <StyledFormHelperTextError>
                            {errorMsg}
                          </StyledFormHelperTextError>
                        </Box>
                      )}
                      {successMsg && (
                        <StyledBox mt={2}>
                          {successMsg}
                        </StyledBox>
                      )}
                      
                      <Box mt={4} display="flex">
                        <StyledButton
                          color="primary"
                          type="submit"
                          variant="contained"
                        >
                          SUBMIT
                        </StyledButton>
                      </Box>
                    </Box>

                    <Box mt={2} display="flex" alignItems="center"
                         sx={{
                          fontSize: "16px",
                          fontStyle: "normal",
                          fontFamily: "Roboto,Helvetica,Arial,sans-serif",
                          fontWeight: "400",
                          lineHeight: "1.7",
                          letterSpacing: "0.00938em",
                        }}>
                      
                        Don't have an account yet?
                        <Link 
                        ml={1}
                        component={RouterLink}
                        to="/signup"
                        color="primary"
                        underline="none"
                      >
                          Sign Up
                        </Link>
                      
                    </Box>
                  </FormSide>

                  </Box>
              </StyledContainer>
            )
          }



                {!isTablet && !isMobile
                  ?
                  (
                    <Box
                      sx={{
                        position: "relative",
                        bottom: "-130px",
                        right: 145,
                        cursor: "pointer",
                      }}
                    >
                      <Box
                        component="img"
                        src={mascotJoin}
                        alt="Join the Fun Mascot Orange"
                        sx={{
                          width: "140px",
                          height: "auto",
                          
                        }}
                      />
                    </Box>
                  )
                  :""
                }

          </Box>
          </form>
        </>
    </Grid>
  );
};

export default ResetPassword;
